/**
 * Klantvereisten-blok (23 okt 2026): de werkafspraken van een klantgroep (bv. Schneider Electric)
 * op de projectpagina, het draaidagdetail, de draaidagkaart en de callsheet.
 *
 * Rustig (Joris: schermen zijn te druk): geen kleurvlak of chips, een dunne grijze lijn links,
 * kleine grijze tekst. Alleen een ontbrekende geheimhoudingsverklaring krijgt kleur.
 * Niets renderen als het project bij geen klantgroep hoort (of de koppeling onzeker is).
 *
 * variant: 'project' (met AI-log, inklapbaar) | 'detail' (drawer, callsheet) | 'compact' (kaart)
 */
import React, { useEffect, useState } from 'react';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { haalKlantVereisten, ndaRegel, ontbrekendOpDag, wisKlantVereisten } from '../utils/klantVereisten';
import { useUserRole } from '../contexts/UserRoleContext';
import AiLogPaneel from './AiLogPaneel';

const KLEUR = { tekst: '#2d3335', stil: '#767c7e', lijn: '#dfe3e6', letop: '#9a4a0b' };
const FONT = '"Inter", system-ui, sans-serif';
const kopSx = {
  fontFamily: '"Manrope", system-ui, sans-serif', fontSize: '0.72rem', fontWeight: 700,
  letterSpacing: '0.12em', textTransform: 'uppercase', color: KLEUR.stil,
};
const linkSx = {
  fontFamily: FONT, fontSize: '0.8rem', color: KLEUR.stil, cursor: 'pointer', textDecoration: 'none',
  border: 0, bgcolor: 'transparent', p: 0, '&:hover': { color: KLEUR.tekst, textDecoration: 'underline' },
};

export default function KlantVereistenBlok({ projectId, nummer, variant = 'project', datum = null, sx = {} }) {
  const [data, setData] = useState(null);
  const [allePunten, setAllePunten] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [versie, setVersie] = useState(0);
  const navigate = useNavigate();
  const { permissions } = useUserRole() || {};
  const magVastleggen = !!permissions?.hr_leveranciers?.edit;

  useEffect(() => {
    let actueel = true;
    if (!projectId && !nummer) { setData(null); return undefined; }
    haalKlantVereisten({ projectId, nummer })
      .then((d) => { if (actueel) setData(d); })
      .catch(() => { if (actueel) setData(null); });   // stil: het blok is aanvullend, geen fouttoast per kaart
    return () => { actueel = false; };
  }, [projectId, nummer, versie]);

  const g = data?.klantgroep;
  if (!g) return null;
  const nda = ndaRegel(data.nda);

  if (variant === 'compact') {
    const namen = ontbrekendOpDag(data.nda, datum);
    return (
      <Tooltip title={(g.punten || []).join(' · ')} placement="top-start">
        <Typography sx={{ fontFamily: FONT, fontSize: '0.78rem', color: KLEUR.stil, mt: 1, ...sx }}>
          {g.titel}: klantvereisten
          {namen.length > 0 && (
            <Box component="span" sx={{ color: KLEUR.letop }}> · geheimhouding ontbreekt ({namen.join(', ')})</Box>
          )}
        </Typography>
      </Tooltip>
    );
  }

  const punten = g.punten || [];
  const zichtbaar = allePunten ? punten : punten.slice(0, variant === 'project' ? 3 : 2);
  const aantalLog = data.aiLog?.aantal ?? 0;

  return (
    <Box component="section" aria-label={`Klantvereisten ${g.titel}`}
      sx={{ borderLeft: `2px solid ${KLEUR.lijn}`, pl: 2, py: 0.25, my: 2.5, ...sx }}>
      <Typography component="h3" sx={{ ...kopSx, mb: 0.75 }}>Werken voor {g.titel}</Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.25, color: KLEUR.stil }}>
        {zichtbaar.map((p) => (
          <Typography component="li" key={p} sx={{ fontFamily: FONT, fontSize: '0.82rem', color: KLEUR.tekst, opacity: 0.85, lineHeight: 1.5 }}>
            {p}
          </Typography>
        ))}
      </Box>
      {nda && (
        <Typography sx={{ fontFamily: FONT, fontSize: '0.82rem', mt: 0.75, color: nda.ernst === 'let-op' ? KLEUR.letop : KLEUR.stil }}>
          {nda.tekst}
          {nda.ernst === 'let-op' && magVastleggen && (
            <>
              {' · '}
              <Link component="button" type="button" onClick={() => navigate('/leveranciers?tab=geheimhouding')} sx={{ ...linkSx, color: KLEUR.letop }}>
                Vastleggen
              </Link>
            </>
          )}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.75 }}>
        {punten.length > zichtbaar.length && (
          <Link component="button" type="button" onClick={() => setAllePunten(true)} sx={linkSx}>
            Alle {punten.length} afspraken
          </Link>
        )}
        {g.wikiUrl && (
          <Link href={g.wikiUrl} target="_blank" rel="noopener noreferrer" sx={linkSx}>Werkafspraken lezen ↗</Link>
        )}
        {variant === 'project' && g.aiLogVerplicht && (
          <Link component="button" type="button" onClick={() => setLogOpen((o) => !o)} sx={linkSx} aria-expanded={logOpen}>
            AI-log ({aantalLog}) {logOpen ? '▴' : '▾'}
          </Link>
        )}
      </Box>
      {variant === 'project' && g.aiLogVerplicht && logOpen && (
        <AiLogPaneel key={`${projectId || ''}|${data.projectNummer || nummer || ''}`} projectId={projectId} nummer={data.projectNummer || nummer} onGewijzigd={() => { wisKlantVereisten(); setVersie((v) => v + 1); }} />
      )}
    </Box>
  );
}
